<template>
    <div :class="{ 'e-datepicker-expanded': expanded }">
        <DatePicker
            v-bind="primeVueProps"
            ref="pvDatepicker"
            :model-value="modelValue"
            @update:model-value="handlePrimeVueDateUpdate"
            @show="$emit('active-change', true)"
            @hide="$emit('active-change', false)"
        >
            <template v-if="events && events.length > 0" #date="slotProps">
                <span :class="getDateClasses(slotProps.date)">
                    {{ slotProps.date.day }}
                    <span v-if="hasEvent(slotProps.date)" class="e-datepicker-indicator" :class="indicatorClass" />
                </span>
            </template>
            <template v-for="slot in getForwardedSlots($slots)" #[slot]="scope">
                <slot :name="slot" v-bind="scope" />
            </template>
        </DatePicker>
    </div>
</template>

<script>
import { computed, ref } from 'vue';
/**
 * EDatepicker - Abstraction wrapper around Buefy's b-datepicker that can also render a PrimeVue Datepicker.
 *
 * This component acts as a translation layer to support props from the Buefy b-datepicker
 * and translate them to PrimeVue DatePicker props.
 *
 * All Buefy b-datepicker props, events, and slots are supported.
 */
export default {
    name: 'EDatepicker',
    inheritAttrs: false,
    props: {
        modelValue: {
            type: [Date, Array],
            default: null,
        },
        // Buefy props to be translated
        placeholder: {
            type: String,
            default: '',
        },
        minDate: {
            type: Date,
            default: null,
        },
        maxDate: {
            type: Date,
            default: null,
        },
        range: {
            type: Boolean,
            default: false,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        firstDayOfWeek: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        unselectableDaysOfWeek: {
            type: Array,
            default: null,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            default: null,
        },
        iconRight: {
            type: String,
            default: null,
        },
        iconRightClickable: {
            type: Boolean,
            default: false,
        },
        mobileNative: Boolean,
        trapFocus: Boolean,
        destroyOnHide: Boolean,
        events: {
            type: Array,
            default: null,
        },
        indicators: {
            type: String,
            default: 'dots',
        },
    },
    emits: ['update:modelValue', 'icon-right-click', 'active-change', 'range-end'],
    setup(props, { attrs, emit }) {
        const pvDatepicker = ref(null);

        const primeVueProps = computed(() => {
            const pvProps = {
                ...attrs,
                placeholder: props.placeholder,
                minDate: props.minDate,
                maxDate: props.maxDate,
                inline: props.inline,
                firstDayOfWeek: props.firstDayOfWeek,
                disabled: props.disabled,
                fluid: props.expanded,
            };

            if (props.range) {
                pvProps.selectionMode = 'range';
            }

            if (props.unselectableDaysOfWeek) {
                pvProps.disabledDays = props.unselectableDaysOfWeek;
            }

            if (props.icon) {
                pvProps.showIcon = true;
            }

            if (props.iconRight) {
                pvProps.showIcon = true;
                pvProps.iconDisplay = 'input';

                if (props.iconRightClickable) {
                    pvProps.pt = pvProps.pt || {};
                    pvProps.pt.inputicon = {
                        root: {
                            onClick: event => emit('icon-right-click', event),
                            class: 'tw:cursor-pointer',
                        },
                    };
                }
            }

            return pvProps;
        });

        const indicatorClass = computed(() => {
            if (!props.indicators) return '';
            return `e-datepicker-indicator--${props.indicators}`;
        });

        const normalizeDate = date => {
            if (!date) return null;
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            return d.getTime();
        };

        const hasEvent = dateObj => {
            if (!props.events || props.events.length === 0) return false;

            // PrimeVue passes {day, month, year} object, not a Date
            let targetDate;
            if (dateObj.day !== undefined && dateObj.month !== undefined && dateObj.year !== undefined) {
                // PrimeVue format: {day: 15, month: 10, year: 2025}
                // Note: month is 0-indexed in PrimeVue
                targetDate = new Date(dateObj.year, dateObj.month, dateObj.day);
            } else {
                targetDate = new Date(dateObj);
            }

            const targetTime = normalizeDate(targetDate);
            return props.events.some(event => {
                const eventTime = normalizeDate(event.date || event);
                return eventTime === targetTime;
            });
        };

        const getDateClasses = dateObj => {
            return {
                'e-datepicker-date-with-event': hasEvent(dateObj),
            };
        };

        const handlePrimeVueDateUpdate = value => {
            emit('update:modelValue', value);
            if (props.range && Array.isArray(value) && value.length === 2 && value[1] != null) {
                emit('range-end', value[1]);
            }
        };

        const toggle = () => {
            if (pvDatepicker.value) {
                if (pvDatepicker.value.overlayVisible) {
                    pvDatepicker.value.hide();
                } else {
                    pvDatepicker.value.show();
                }
            }
        };

        const getForwardedSlots = slots => {
            const keys = Object.keys(slots);
            if (props.events && props.events.length > 0) {
                return keys.filter(key => key !== 'date');
            }
            return keys;
        };

        return {
            primeVueProps,
            handlePrimeVueDateUpdate,
            pvDatepicker,
            toggle,
            indicatorClass,
            hasEvent,
            getDateClasses,
            getForwardedSlots,
        };
    },
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.e-datepicker-date-with-event {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.e-datepicker-indicator {
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
}

.e-datepicker-indicator--dots {
    @apply tw:w-1 tw:h-1 tw:rounded-full tw:bg-primary;
}

.e-datepicker-indicator--bars {
    @apply tw:w-4/5 tw:h-0.5 tw:bg-primary tw:rounded-sm;
}
</style>
